/**
 * 合集双封面 exta 键（与后端 Registry slot 对齐；无旧版 cover_image 兼容）。
 * 列表 thumbUrl 默认取女封面。
 */
export const COLLECTION_COVER_EXTA_KEYS = {
  female: 'cover_image_female',
  male: 'cover_image_male',
}

export const COLLECTION_COVER_THUMB_KEY = COLLECTION_COVER_EXTA_KEYS.female

/** 合集各区绑定条数上限（与后端 WorkoutCollectionBindingValidator 一致） */
export const MAX_BINDINGS_BY_ZONE = {
  actions: 20,
  music: 5,
  voices: 3,
}

/** @deprecated 仅动作区上限；新区请用 getMaxBindingsForZone */
export const MAX_BINDINGS_PER_ZONE = MAX_BINDINGS_BY_ZONE.actions

export function getMaxBindingsForZone(zoneId) {
  return MAX_BINDINGS_BY_ZONE[zoneId] ?? MAX_BINDINGS_BY_ZONE.actions
}

/**
 * 运营端 exta 字段展示名（驼峰）。
 * JSON 契约键仍为 snake_case（如 display_name）；仅 UI 标签用驼峰。
 */
export const WORKOUT_FIELD_LABELS = {
  displayName: 'displayName',
  difficulty: 'difficulty',
  description: 'description',
  videoLoop: 'videoLoop',
  loopable: 'loopable',
  videoDurationSeconds: 'videoDurationSeconds',
  /** 动作代谢当量 MET；App 估算卡路里，CMS 不再维护 calories / 合集总卡路里 */
  met: 'MET',
  gender: 'gender',
  language: 'language',
  subtitle: 'subtitle',
  coverImage: 'coverImage',
  /** 合集女/男封面（exta: cover_image_female / cover_image_male） */
  coverImageFemale: 'coverImageFemale',
  coverImageMale: 'coverImageMale',
  durationSeconds: 'durationSeconds',
  repeatCount: 'repeatCount',
  leftoverSeconds: 'leftoverSeconds',
  configuredPlaySeconds: 'configuredPlaySeconds',
  audioDurationSeconds: 'audioDurationSeconds',
  loop: 'loop',
  restSeconds: 'restSeconds',
  totalDurationSeconds: 'totalDurationSeconds',
  resourceName: 'resourceName',
  actions: 'actions',
  music: 'music',
  voices: 'voices',
  zipPackage: 'zipPackage',
  /** 动作切换口令 / 执行引导 mp3（exta: switch_voice_url / guide_voice_url；可选） */
  switchVoiceUrl: 'switchVoiceUrl',
  guideVoiceUrl: 'guideVoiceUrl',
}

/**
 * 合集 binding 运营界面中文标签（落库键仍为 snake_case）。
 * duration_seconds：单视角 = repeat × 主；双视角 = repeat ×（主+侧）；运营不再手填。
 */
export const WORKOUT_BINDING_FIELD_LABELS = {
  order: '训练顺序',
  videoRepeatCount: '视频循环次数',
  videoDurationSeconds: '动作视频时长',
  configuredPlaySeconds: '视频播放总长',
  audioDurationSeconds: '音频时长',
  loop: '合集内循环',
  restSeconds: '休息时长',
  totalDurationSeconds: '总时长',
}

/** description 输入框 placeholder（聚焦即消失，不下发 App） */
export const WORKOUT_DESCRIPTION_PLACEHOLDERS = {
  workout_action: '填写该动作的介绍说明，内容将下发给 App 展示',
  workout_collection: '填写该课程（合集）的介绍说明，内容将下发给 App 展示',
}

/** 布尔/开关类字段：meta 条底栏通栏说明（右对齐，不下发 App） */
export const WORKOUT_FIELD_TIPS = {
  videoLoop:
    'videoLoop：true = 动作视频可无缝循环；false 时该动作在合集中 repeat_count 最多为 1',
  // MET = Metabolic Equivalent of Task（https://en.wikipedia.org/wiki/Metabolic_equivalent）
  met: 'MET：代谢当量，表示该动作相对静息的能量消耗倍数；卡路里由 App 根据 MET、体重与运动时长估算，CMS 只填 MET',
  loopable:
    'loopable：true = 音乐适合循环播放；false 时合集中 loop 只能为 false',
}

/** 合集 binding 区字段小提示（动作区头部两行图例；音乐区头部底栏，不下发 App） */
export const WORKOUT_BINDING_FIELD_TIPS = {
  videoRepeatCount:
    '单视角：主视频播几遍；双视角：主+侧为一组，播几组。不可循环的视频最多 1',
  configuredPlaySeconds:
    '单视角 = 主时长×次数；双视角 =（主+侧）×次数，保存时自动计算',
  restSeconds: '该动作结束后休息几秒；最后一条动作无需配置休息',
  collectionLoop:
    'loop：BGM 播完后是否在合集中继续循环；仅当音乐资源 loopable=true 时可设为 true',
  totalDurationSeconds: '所有动作运动时长 + 中间休息时长之和，保存时自动计算',
  mainSideMismatch: '主侧时长不一致：总时长仍按（主+侧）×循环次数计算',
}

/** workout 语音 language 下拉选项（运营不手填） */
export const WORKOUT_LANGUAGE_OPTIONS = [
  { label: '中文 (zh)', value: 'zh' },
  { label: 'English (en)', value: 'en' },
  { label: '繁體中文 (zh-TW)', value: 'zh-TW' },
  { label: '日本語 (ja)', value: 'ja' },
  { label: '한국어 (ko)', value: 'ko' },
  { label: 'Español (es)', value: 'es' },
  { label: 'Français (fr)', value: 'fr' },
  { label: 'Deutsch (de)', value: 'de' },
  { label: 'Português (pt)', value: 'pt' },
  { label: 'Italiano (it)', value: 'it' },
  { label: 'Русский (ru)', value: 'ru' },
  { label: 'العربية (ar)', value: 'ar' },
]

export const WORKOUT_GENDER_OPTIONS = [
  { label: 'female', value: 'female' },
  { label: 'male', value: 'male' },
  { label: 'neutral', value: 'neutral' },
]

/** action / collection 共用难度下拉（仅英文落库） */
export const WORKOUT_DIFFICULTY_OPTIONS = [
  { label: 'Beginner', value: 'Beginner' },
  { label: 'Intermediate', value: 'Intermediate' },
  { label: 'Advanced', value: 'Advanced' },
]

/** 旧档 → 新档；编辑加载与展示时把历史 easy/medium/hard 对齐到现行三档 */
const LEGACY_WORKOUT_DIFFICULTY_MAP = {
  easy: 'Beginner',
  medium: 'Intermediate',
  hard: 'Advanced',
  Easy: 'Beginner',
  Medium: 'Intermediate',
  Hard: 'Advanced',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
}

/**
 * 归一化 workout 难度字符串。
 * 幸福路径：已是 Beginner/Intermediate/Advanced → 原样返回。
 * 边界：旧 easy/medium/hard（及大小写变体）映射到新档；空串保持空；未知值原样保留以免误伤。
 */
export function normalizeWorkoutDifficulty(raw) {
  if (raw == null) {
    return ''
  }
  const text = String(raw).trim()
  if (!text) {
    return ''
  }
  return LEGACY_WORKOUT_DIFFICULTY_MAP[text] || text
}
